import { useCallback, useEffect, useRef, useState } from "react";

export const useCountdown = (initialState: number, callback?: () => void) => {
	const [isRunning, setIsRunning] = useState(true);
	const [countdown, setCountdown] = useState(initialState);
	const savedCallback = useRef(callback);

	// `holdSeconds` parks the counter above `initialState`; the returned value is capped at `initialState`,
	// so the countdown waits at its full value while the extra seconds tick away — no second timer.
	const handleResetTimeout = useCallback(
		(shouldRun?: boolean, holdSeconds = 0) => {
			setCountdown(initialState + holdSeconds);
			if (shouldRun !== undefined) {
				setIsRunning(shouldRun);
			}
		},
		[initialState],
	);

	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	useEffect(() => {
		setCountdown(initialState);
	}, [initialState]);

	useEffect(() => {
		if (!isRunning) {
			return;
		}
		if (countdown <= 0) {
			savedCallback.current?.();
			setIsRunning(false);
			return;
		}
		const timer = setTimeout(() => {
			setCountdown((prevState) => prevState - 1);
		}, 1000);
		return () => clearTimeout(timer);
	}, [isRunning, countdown]);

	return {
		countdown: Math.min(countdown, initialState),
		startCountdown: setIsRunning,
		resetCountdown: handleResetTimeout,
	};
};
